import { Market, NumberFormat } from "src/model/shop.model";
import { APP_DEFAULT_CURRENCY_FORMAT } from "./constants";
import { useCurrencyLocale,CurrencyLocale } from "src/hooks/Utils/Shop/useCurrencyLocale";
import { getCurrentLanguage } from "./Market";
import { PriceItxDto } from "src/api/customer";
import { ProductPriceDto } from "src/api/search";

export function getDecimalsQuantity(format: string): number {
  const zerosQuantity = format ? (format.match(/0/g) || []).length : 0;
  return zerosQuantity === 0 || zerosQuantity === 1 ? 0 : zerosQuantity - 1;
}

export function getSeparators(format: string) {
  const matchHashtags = format.match(/#([^#]+)#/);
  const matchCeros = format.match(/0([^0]+)0/);
  return {
    thousandsSeparator:
      matchHashtags && matchHashtags.length > 0 ? matchHashtags[1] : "",
    decimalSeparator: matchCeros && matchCeros.length > 0 ? matchCeros[1] : "",
  };
}

function formatDotsAndCommas(
  formattedPrice: string,
  thousandsSeparator: string,
  centSeparator: string
) {
  let preformattedThousands = formattedPrice.replace(/\,/g, "-");
  let draftFormatting = preformattedThousands.replace(/\./g, "|");

  return draftFormatting
    .replace(/\|/g, centSeparator)
    .replace(/-/g, thousandsSeparator);
}

function getDecimalCorrectlyStringPrice(
  price: number,
  formattedDecimalsQuantity: number
) {
  let decimalCorrectlyPrice: string = "";
  if (formattedDecimalsQuantity === 0) {
    decimalCorrectlyPrice = price.toLocaleString("en-US");
  } else if (formattedDecimalsQuantity === 1) {
    decimalCorrectlyPrice = (Math.floor(price * 10) / 10).toLocaleString("en-US");
  } else {
    decimalCorrectlyPrice = price.toFixed(formattedDecimalsQuantity);
  }
  return decimalCorrectlyPrice;
}

function findCurrencySpace (end?:boolean, currencyFormat?: string) {
  const result = end ? currencyFormat.match(/(.)¤+/) :currencyFormat.match(/¤+(.)/);
  return result?.length > 0 ? result[1] === " " ? result[1] : "" : " ";
}

export const formatStartWithSymbol = () => {
  const { currencyFormat } = useCurrencyLocale();
  return currencyFormat.startsWith("¤");
}

export const formatEndWithSymbol = () => {
  const { currencyFormat } = useCurrencyLocale();
  return currencyFormat.endsWith("¤");
}

export function addCurrencyToPrice(
  currencyLocale: CurrencyLocale,
  price: string
): string {
  const { currencyFormat, currencySymbol } = currencyLocale;

  if (formatStartWithSymbol()) {
    const space = findCurrencySpace(false, currencyFormat)
    price = currencySymbol + space + price;
  } else if (formatEndWithSymbol()) {
    const space = findCurrencySpace(true, currencyFormat)
    price += space + currencySymbol;
  }
  return price;
}

export function renderPrice(price: {}, withoutCurrency?: boolean, roundUp?: boolean): string {
  const currencyLocale = useCurrencyLocale();
  const formatedPrice = formatPrice(currencyLocale, price, roundUp);

  if (withoutCurrency) {
    return formatedPrice;
  }
  const formatedPriceWithCurrency = addCurrencyToPrice(
    currencyLocale,
    formatedPrice
  );
  
  return formatedPriceWithCurrency;
}

export function formatPrice(currencyLocale: CurrencyLocale, priceObj: PriceItxDto, roundUp: boolean) {
  const { currencyFormat, currencySeparators } = currencyLocale;
  if(!!priceObj){
  const { value, exponent } = priceObj;
  let defaultMask = "#,##0.00 ¤¤";
  let formattedDecimalsQuantity = getDecimalsQuantity(currencyFormat);
  let formattedPrice = "";
  let formattedPriceToNumber = value !== 0 ? Number(value) : value;
  let newFormat = currencyFormat
    ? currencyFormat.replace(/ /g, "")
    : defaultMask.replace(/ /g, "");
  let formattedExponentToNumber = exponent !== 0 ? Number(exponent) : exponent;

  const price = formattedPriceToNumber
    ? formattedPriceToNumber / 10 ** formattedExponentToNumber
    : 0;

  let decimalsCorrectlyStringPrice = getDecimalCorrectlyStringPrice(
    price,
    formattedDecimalsQuantity
  );

  if(roundUp){
    decimalsCorrectlyStringPrice = Math.ceil(Number(decimalsCorrectlyStringPrice)).toLocaleString("en-US");
  }

  let separators = currencySeparators
    ? currencySeparators
    : getSeparators(newFormat);

  formattedPrice = formatDotsAndCommas(
    decimalsCorrectlyStringPrice,
    separators.thousandsSeparator,
    separators.decimalSeparator
  );

  formattedPrice = formattedPrice.includes(separators.decimalSeparator)
    ? formattedPrice
    : formattedPrice +
      separators.decimalSeparator +
      "0".repeat(formattedDecimalsQuantity);
    return formattedPrice;

  } else {
    return ""
  }
}

export function getCurrencySeparators(market: Market): NumberFormat {
  const currencyLanguage = getCurrentLanguage(market);
  const currencyLanguageInfo = market?.languages?.availableLanguages.find(
    (availableLanguage) => availableLanguage?.code === currencyLanguage
  );
  return currencyLanguageInfo?.formats?.number
    ? currencyLanguageInfo.formats.number
    : null;
}

export function getCurrencySymbol(market: Market): string {
  return market?.locale?.availableCurrencies?.length > 0
    ? market?.locale?.availableCurrencies[0]?.currencySymbol
    : "";
}

export function getCurrencyFormat(market: Market): string {
  return market?.locale?.availableLocales?.length > 0
    ? market?.locale?.availableCurrencies[0]?.currencyFormat
    : APP_DEFAULT_CURRENCY_FORMAT;
}

export function getCurrencyDecimals(market: Market): number {
  return market?.locale?.availableLocales?.length > 0
    ? Math.abs(parseInt(market?.locale?.availableCurrencies[0]?.currencyDecimals))
    : 0;
}
export function getCurrencyCode(market: Market): string {
  return market?.locale?.availableLocales?.length > 0
    ? market?.locale?.availableCurrencies[0]?.currencyCode
    : "";
}

export const formatPriceForInput = (
  priceObject: PriceItxDto,
  currencySeparators: NumberFormat
) => {
  const price = priceObject.value.toString();
  const position = price.length - priceObject.exponent;
  return [
    price.slice(0, position),
    currencySeparators?.decimalSeparator,
    price.slice(position),
  ].join("");
};

export const renderWritePrice = (
  newValue: string,
  currencyLocale: CurrencyLocale
): string => {
  if (!currencyLocale?.currencySeparators) {
    return newValue;
  }
  const {
    currencyFormat,
    currencySeparators: { decimalSeparator },
  } = currencyLocale;
  const formattedDecimalsQuantity = getDecimalsQuantity(currencyFormat);
  return newValue.indexOf(decimalSeparator) >= 0
    ? newValue.slice(
        0,
        newValue.indexOf(decimalSeparator) + formattedDecimalsQuantity + 1
      )
    : newValue;
};

const hasCorrectlyDecimalSeparator = (
  price: string,
  currencyLocale: CurrencyLocale
): boolean => {
  const {
    currencySeparators: { decimalSeparator },
  } = currencyLocale;
  let hasCorrectlyDecimalSeparator = true;
  if (price.includes(",") || price.includes("."))
    if (decimalSeparator === ".") {
      hasCorrectlyDecimalSeparator =
        price.includes(decimalSeparator) && !price.includes(",");
    } else if (decimalSeparator === ",") {
      hasCorrectlyDecimalSeparator =
        price.includes(decimalSeparator) && !price.includes(".");
    }
  return hasCorrectlyDecimalSeparator;
};

export const validatePrice = (
  price: string,
  currencyLocale: CurrencyLocale
): boolean => {
  let notValid = true;

  if (hasCorrectlyDecimalSeparator(price, currencyLocale)) {
    let valueWithOutSeparatos = price.replace(/[,.\s]/g, ".");

    notValid = !(Number(valueWithOutSeparatos) + 1);
  }
  return notValid;
};

type pricesFilterTypeParams = {
  minPrice: ProductPriceDto;
  maxPrice: ProductPriceDto;
  currencyLocale: CurrencyLocale;
}

export type filterPrices = {
  maxPrice: number;
  minPrice: number;
  originalMinPrice: ProductPriceDto;
  originalMaxPrice: ProductPriceDto;
}

export const getPricesFilter = ({minPrice, maxPrice, currencyLocale}: pricesFilterTypeParams): filterPrices => {
  return {
    maxPrice: + renderPrice(maxPrice, true, true)
      .split(currencyLocale.currencySeparators.thousandsSeparator)
      .join("")
      .split(currencyLocale.currencySeparators.decimalSeparator)[0],
    minPrice: + renderPrice(minPrice, true)
      .split(currencyLocale.currencySeparators.thousandsSeparator)
      .join("")
      .split(currencyLocale.currencySeparators.decimalSeparator)[0],
    originalMinPrice: minPrice,
    originalMaxPrice: maxPrice,
  }
}

export const getPriceFormat = (price: string): PriceItxDto => {
  let priceExponent = 0;
  const { currencySymbol, currencySeparators } = useCurrencyLocale();

  if (price?.includes(currencySeparators?.decimalSeparator)) {
    const pricePartsArray = price?.split(currencySeparators?.decimalSeparator);
    priceExponent = pricePartsArray?.[pricePartsArray?.length - 1]?.length;
  }
  return {
    currency: currencySymbol,
    value: Number(price?.replace(/\.|\,/g, '')),
    exponent: priceExponent,
  }
}
